// Udostępnianie sztuk (SPIS D-5, S-3; worker-go/api/udostepnienia.go, zaproszenia.go). Zaproszenia do mnie - stan poza
// Reactem: wczytywany przy starcie i po zdarzeniu SSE `konto` (`udostepnienia.zmiana`), licznik pokazuje ekran
// Urządzenia. Ekran udostępniania sztuki słucha tego samego zdarzenia (`useWersjaUdostepnien`).
import { useSyncExternalStore } from "react";
import type { AcceptReply, AccountEvent, InvitationOut, InvitationsReply, InviteReply, OKReply, SharesReply, Uprawnienie } from "../gen/kontrakt";
import { zapytanie } from "./zaplecze";

const sciezka = (sn: string) => "/api/udostepnienia/" + encodeURIComponent(sn);

export const udostepnienia = (sn: string) => zapytanie<SharesReply>("GET", sciezka(sn));

/** Zaproszenie adresu; `do` - RRRR-MM-DD albo "" (bezterminowo). */
export const zapros = (sn: string, email: string, uprawnienie: Uprawnienie, doDnia: string) =>
  zapytanie<InviteReply>("POST", sciezka(sn), { email, uprawnienie, do: doDnia });

export const odwolaj = (sn: string, id: number) => zapytanie<OKReply>("DELETE", sciezka(sn) + "/" + id);

let zaproszenia: readonly InvitationOut[] = [];
let wersja = 0; // rośnie przy każdej zmianie udostępnień konta (ekrany czytają wtedy od nowa)
const sluchacze = new Set<() => void>();
let wczytywanie: Promise<void> | null = null;

function zmien(lista?: readonly InvitationOut[]): void {
  if (lista) zaproszenia = lista;
  wersja++;
  for (const f of sluchacze) f();
}

/** Wczytuje zaproszenia do mnie (jedno zapytanie naraz); błąd - zostaje poprzednia lista. */
export function wczytajZaproszenia(): Promise<void> {
  wczytywanie ??= zapytanie<InvitationsReply>("GET", "/api/zaproszenia")
    .then((o) => zmien(o.zaproszenia), () => undefined)
    .finally(() => { wczytywanie = null; });
  return wczytywanie;
}

/** Zdarzenie SSE `konto`: udostępnienia konta zmienione (tu albo na innym telefonie). */
export function przyjmijZdarzenieUdostepnien(e: AccountEvent): void {
  if (e.rodzaj === "udostepnienia.zmiana") void wczytajZaproszenia();
}

/** Kod z linku w e-mailu przypisuje zaproszenie do konta. */
export const odbierzKod = (kod: string) => zapytanie<OKReply>("POST", "/api/zaproszenia/kod", { kod });

export async function przyjmij(id: number): Promise<string> {
  const o = await zapytanie<AcceptReply>("POST", `/api/zaproszenia/${id}/przyjmij`);
  zmien(zaproszenia.filter((z) => z.id !== id));
  return o.sn;
}

export async function odrzuc(id: number): Promise<void> {
  await zapytanie<AcceptReply>("POST", `/api/zaproszenia/${id}/odrzuc`);
  zmien(zaproszenia.filter((z) => z.id !== id));
}

/** Wylogowanie: bez zaproszeń poprzedniego konta. */
export function wyczyscZaproszenia(): void {
  zmien([]);
}

function subskrybuj(f: () => void): () => void {
  sluchacze.add(f);
  return () => sluchacze.delete(f);
}

export function useZaproszenia(): readonly InvitationOut[] {
  return useSyncExternalStore(subskrybuj, () => zaproszenia);
}

/** Numer zmiany udostępnień - ekran udostępniania sztuki czyta listę od nowa, gdy rośnie. */
export function useWersjaUdostepnien(): number {
  return useSyncExternalStore(subskrybuj, () => wersja);
}
